/**
 * WinLoss — close rate by lead source + why deals were lost.
 * Data: GET /api/reports/win-loss
 */
import React, { useEffect, useState } from "react";
import { api, formatCurrency } from "@/lib/api";
import { TrendingUp } from "lucide-react";

export default function WinLoss() {
  const [data, setData] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/reports/win-loss", { params }).then((r) => setData(r.data)).catch(() => setData({ by_source: [], loss_reasons: [] }));
  }, [dateFrom, dateTo]);

  if (!data) return <div className="p-8 text-xs uppercase tracking-[0.2em] text-zinc-500">Loading…</div>;
  const maxReason = Math.max(1, ...data.loss_reasons.map((r) => r.count));

  return (
    <div className="p-6 lg:p-8" data-testid="win-loss-page">
      <div className="flex items-center gap-3 mb-2">
        <TrendingUp className="w-6 h-6 text-blue-700" />
        <h1 className="font-heading text-2xl font-black tracking-tight">Win / Loss</h1>
      </div>
      <p className="text-xs text-zinc-500 mb-4">Close rate by lead source, and why lost deals were lost. Close rate = won ÷ (won + lost) — open deals don't count against you.</p>

      <div className="flex flex-wrap items-end gap-3 mb-5" data-testid="win-loss-date-filter">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Deals created from</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="h-9 px-2 border border-zinc-300 rounded-sm text-sm bg-white" data-testid="win-loss-date-from" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="h-9 px-2 border border-zinc-300 rounded-sm text-sm bg-white" data-testid="win-loss-date-to" />
        </div>
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(""); setDateTo(""); }}
            className="h-9 px-3 text-xs font-bold uppercase tracking-wider border border-zinc-300 rounded-sm hover:border-zinc-950"
            data-testid="win-loss-date-clear">
            Clear — all time
          </button>
        )}
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto mb-8">
        <table className="w-full text-sm" data-testid="win-loss-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
              <th className="px-4 py-2.5">Lead Source</th>
              <th className="px-3 py-2.5 text-right">Deals</th>
              <th className="px-3 py-2.5 text-right">Won</th>
              <th className="px-3 py-2.5 text-right">Lost</th>
              <th className="px-3 py-2.5 text-right">Open</th>
              <th className="px-3 py-2.5 text-right">Won Value</th>
              <th className="px-4 py-2.5 text-right">Close Rate</th>
            </tr>
          </thead>
          <tbody>
            {data.by_source.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-zinc-400">No deals yet.</td></tr>
            )}
            {data.by_source.map((s) => (
              <tr key={s.source} className="border-b border-zinc-50 hover:bg-zinc-50" data-testid={`win-loss-row-${s.source}`}>
                <td className="px-4 py-2.5 font-bold">{s.source}</td>
                <td className="px-3 py-2.5 text-right font-mono">{s.total}</td>
                <td className="px-3 py-2.5 text-right font-mono text-emerald-700 font-bold">{s.won}</td>
                <td className="px-3 py-2.5 text-right font-mono text-red-700 font-bold">{s.lost}</td>
                <td className="px-3 py-2.5 text-right font-mono text-zinc-500">{s.open}</td>
                <td className="px-3 py-2.5 text-right font-mono">{formatCurrency(s.won_value)}</td>
                <td className="px-4 py-2.5 text-right">
                  {s.close_rate === null ? (
                    <span className="text-[10px] text-zinc-400 uppercase">no decisions yet</span>
                  ) : (
                    <span className={`font-mono font-bold ${s.close_rate >= 50 ? "text-emerald-700" : "text-zinc-800"}`}>{s.close_rate}%</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="text-base font-black tracking-tight mb-3">Why We Lose</h2>
      {data.loss_reasons.length === 0 ? (
        <div className="bg-white border border-dashed border-zinc-200 rounded-sm p-6 text-sm text-zinc-500" data-testid="loss-reasons-empty">
          No lost deals with reasons yet. When you mark a deal Lost, pick a reason — it shows up here.
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-sm p-5 space-y-3 max-w-xl" data-testid="loss-reasons-list">
          {data.loss_reasons.map((r) => (
            <div key={r.reason} className="flex items-center gap-3" data-testid={`loss-reason-${r.reason}`}>
              <span className="w-36 shrink-0 text-xs font-bold">{r.reason}</span>
              <div className="flex-1 h-2 bg-zinc-100 rounded-sm overflow-hidden">
                <div className="h-full bg-red-600" style={{ width: `${(r.count / maxReason) * 100}%` }} />
              </div>
              <span className="font-mono text-xs font-bold w-6 text-right">{r.count}</span>
            </div>
          ))}
        </div>
      )}
      <div className="text-[10px] text-zinc-400 mt-4">Website leads are tagged automatically. Set the source on other deals from the Edit Deal form.</div>
    </div>
  );
}
